import {
  Navigate,
  Outlet,
  useParams,
  useLocation,
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MainLayout from "./layout/MainLayout";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import InventoryDetail from "./pages/InventoryDetail";
import AssetDetail from "./pages/AssetDetail";
import Login from "./pages/Login";
import SignupFlow from "./pages/SignupFlow";
import Profile from "./pages/Profile";
// import CreateOrganization from "./pages/CreateOrganization";
import Settings from "./pages/Settings";
import UserSettings from "./pages/UserSettings";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import JobNew from "./pages/JobNew";
import Calendar from "./pages/Calendar";
import Crew from "./pages/Crew";
import CrewDetail from "./pages/CrewDetail";
import CrewNew from "./pages/CrewNew";
import CrewEdit from "./pages/CrewEdit";
import Picklist from "./pages/Picklist";
import JobSelection from "./pages/JobSelection";
import CheckIn from "./pages/CheckIn";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useEffect, useState, useRef } from "react";
import LoadingScreen from "./components/LoadingScreen";
import InvitationBanner from "./components/InvitationBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [loadingMessage, setLoadingMessage] = useState("Authenticating...");
  const [showLoading, setShowLoading] = useState(true);
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState(location.pathname);
  const isTabNavigation = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log("ProtectedRoute - User:", user?.id);
    console.log("ProtectedRoute - User metadata:", user?.user_metadata);
  }, [user]);

  // Check if this is a tab navigation
  useEffect(() => {
    // If we're just navigating between tabs, don't show loading
    if (previousPath === location.pathname) {
      isTabNavigation.current = true;
    } else {
      isTabNavigation.current = false;
      setPreviousPath(location.pathname);
    }
  }, [location.pathname, previousPath]);

  // Simulate a loading sequence with different messages
  useEffect(() => {
    // Skip loading for tab navigation
    if (isTabNavigation.current) {
      setShowLoading(false);
      return;
    }

    if (loading) {
      setShowLoading(true);
      const timer1 = setTimeout(() => {
        setLoadingMessage("Checking credentials...");
      }, 800);

      const timer2 = setTimeout(() => {
        setLoadingMessage("Almost there...");
      }, 1600);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      // Add a small delay before hiding the loading screen for a smoother transition
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading]);

  // Skip loading for tab navigation
  if (isTabNavigation.current) {
    console.log("ProtectedRoute - Tab navigation, skipping loading");
  } else if (showLoading && loading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

// Organization Context Route wrapper
function OrganizationContextRoute() {
  const { orgId } = useParams();
  const {
    user,
    loading,
    currentOrganization,
    switchOrganization,
    userOrganizations,
    pendingInvitations,
  } = useAuth();
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Loading organization data..."
  );
  const [showLoading, setShowLoading] = useState(true);
  const [previousOrgId, setPreviousOrgId] = useState(orgId);
  const isTabNavigation = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log("OrganizationContextRoute - User:", user?.id);
    console.log(
      "OrganizationContextRoute - User metadata:",
      user?.user_metadata
    );
    console.log(
      "OrganizationContextRoute - Current Organization:",
      currentOrganization
    );
  }, [user, currentOrganization]);

  useEffect(() => {
    // Check if this is a tab navigation within the same organization
    if (previousOrgId === orgId && currentOrganization) {
      isTabNavigation.current = true;
    } else {
      isTabNavigation.current = false;
      setPreviousOrgId(orgId);
    }

    // If we have a valid organization ID in the URL and it's different from current org
    if (orgId && currentOrganization?.id !== orgId) {
      // Check if this organization ID belongs to the user
      const orgExists = userOrganizations.some(
        (org) => org.organization_id === orgId
      );

      if (orgExists) {
        // Switch to this organization
        setSwitchingOrg(true);
        switchOrganization(orgId).finally(() => {
          setSwitchingOrg(false);
        });
      }
    }
  }, [
    orgId,
    currentOrganization,
    switchOrganization,
    userOrganizations,
    previousOrgId,
  ]);

  // Simulate a loading sequence with different messages
  useEffect(() => {
    // Skip loading screen for tab navigation within the same organization
    if (isTabNavigation.current) {
      setShowLoading(false);
      return;
    }

    if (loading || switchingOrg) {
      setShowLoading(true);
      const timer1 = setTimeout(() => {
        setLoadingMessage("Preparing your workspace...");
      }, 800);

      const timer2 = setTimeout(() => {
        setLoadingMessage("Almost there...");
      }, 1600);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      // Add a small delay before hiding the loading screen for a smoother transition
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading, switchingOrg]);

  // Skip loading screen for tab navigation
  if (isTabNavigation.current) {
    console.log("OrganizationContextRoute - Tab navigation, skipping loading");
  } else if (showLoading && (loading || switchingOrg)) {
    return <LoadingScreen message={loadingMessage} />;
  }

  if (!user) {
    console.log("OrganizationContextRoute - Redirecting to login");
    return <Navigate to="/login" />;
  }

  if (!currentOrganization) {
    console.log(
      "OrganizationContextRoute - Redirecting to create organization"
    );
    return <Navigate to="/organizations/create" />;
  }

  // If URL doesn't have an org ID, redirect to the URL with the current org ID
  if (!orgId) {
    console.log(
      "OrganizationContextRoute - Redirecting to current organization"
    );
    return <Navigate to={`/org/${currentOrganization.id}`} replace />;
  }

  // If URL has a different org ID than the current one, wait for the switch to complete
  if (orgId !== currentOrganization.id && !isTabNavigation.current) {
    return <LoadingScreen message="Switching organization..." />;
  }

  return (
    <>
      {pendingInvitations && pendingInvitations.length > 0 && (
        <InvitationBanner />
      )}
      <Outlet />
    </>
  );
}

// ScrollToTop component to prevent scrolling on navigation
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Only prevent automatic scrolling for tab navigation
    // We can identify tab navigation by checking if the pathname contains specific patterns
    const isTabNavigation = /\/org\/[^/]+\/[^/]+$/.test(pathname);

    if (isTabNavigation) {
      // For tab navigation, don't reset scroll
      window.history.scrollRestoration = "manual";
    } else {
      // For other navigation (like going to detail pages), allow scroll reset
      window.history.scrollRestoration = "auto";
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

function AppRoutes() {
  // Create router with options to prevent page refreshes
  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: <ScrollToTopWrapper />,
        children: [
          {
            path: "login",
            element: <Login />,
          },
          {
            path: "signup",
            element: <SignupFlow />,
          },
          {
            element: (
              <ProtectedRoute>
                <MainLayout>
                  <Outlet />
                </MainLayout>
              </ProtectedRoute>
            ),
            children: [
              // Temporarily hidden - moved to signup flow
              // {
              //   path: "organizations/create",
              //   element: <CreateOrganization />,
              // },
              {
                path: "profile",
                element: <Profile />,
              },
              {
                path: "user/settings",
                element: <UserSettings />,
              },
            ],
          },
          {
            path: "org/:orgId",
            element: (
              <ProtectedRoute>
                <MainLayout>
                  <OrganizationContextRoute />
                </MainLayout>
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <Dashboard />,
              },
              {
                path: "dashboard",
                element: <Dashboard />,
              },
              {
                path: "inventory",
                element: <Inventory />,
              },
              {
                path: "inventory/:id",
                element: <InventoryDetail />,
              },
              {
                path: "asset/:assetId",
                element: <AssetDetail />,
              },
              {
                path: "settings",
                element: <Settings />,
              },
              {
                path: "jobs",
                element: <Jobs />,
              },
              {
                path: "jobs/new",
                element: <JobNew />,
              },
              {
                path: "jobs/:jobId",
                element: <JobDetail />,
              },
              {
                path: "picklist",
                element: <JobSelection />,
              },
              {
                path: "picklist/:jobId",
                element: <Picklist />,
              },
              {
                path: "checkin",
                element: <CheckIn />,
              },
              {
                path: "calendar",
                element: <Calendar />,
              },
              {
                path: "crew",
                element: <Crew />,
              },
              {
                path: "crew/new",
                element: <CrewNew />,
              },
              {
                path: "crew/:id",
                element: <CrewDetail />,
              },
              {
                path: "crew/:id/edit",
                element: <CrewEdit />,
              },
            ],
          },
          {
            index: true,
            element: (
              <ProtectedRoute>
                <RootRedirect />
              </ProtectedRoute>
            ),
          },
          {
            path: "*",
            element: <Navigate to="/" replace />,
          },
        ],
      },
    ],
    {
      // Use basename if needed
      basename: "/",
    }
  );

  return <RouterProvider router={router} />;
}

// Wrapper component to include ScrollToTop with Outlet
function ScrollToTopWrapper() {
  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
}

// Helper component to redirect from root to the appropriate path
function RootRedirect() {
  const {
    user,
    currentOrganization,
    loading,
    userOrganizations,
    switchOrganization,
  } = useAuth();
  const [loadingMessage, setLoadingMessage] = useState(
    "Loading your workspace..."
  );
  const [showLoading, setShowLoading] = useState(true);
  const [isSettingOrg, setIsSettingOrg] = useState(false);
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState(location.pathname);
  const isTabNavigation = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log("RootRedirect - User:", user?.id);
    console.log("RootRedirect - User metadata:", user?.user_metadata);
    console.log("RootRedirect - Current Organization:", currentOrganization);
    console.log("RootRedirect - Loading:", loading);
    console.log("RootRedirect - User Organizations:", userOrganizations);
  }, [user, currentOrganization, loading, userOrganizations]);

  // Check if this is a tab navigation
  useEffect(() => {
    // If we're just navigating between tabs, don't show loading
    if (previousPath === location.pathname) {
      isTabNavigation.current = true;
    } else {
      isTabNavigation.current = false;
      setPreviousPath(location.pathname);
    }
  }, [location.pathname, previousPath]);

  // Simulate a loading sequence with different messages
  useEffect(() => {
    // Skip loading for tab navigation
    if (isTabNavigation.current) {
      setShowLoading(false);
      return;
    }

    if (loading) {
      setShowLoading(true);
      const timer1 = setTimeout(() => {
        setLoadingMessage("Checking your account...");
      }, 800);

      const timer2 = setTimeout(() => {
        setLoadingMessage("Almost there...");
      }, 1600);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      // Add a small delay before hiding the loading screen for a smoother transition
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading]);

  // Skip loading for tab navigation
  if (isTabNavigation.current) {
    console.log("RootRedirect - Tab navigation, skipping loading");
  } else if (showLoading && loading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  // Don't redirect if still loading
  if (loading) {
    return <LoadingScreen message="Loading your workspace..." />;
  }

  // If user has organizations but no current organization is set, try to set it
  if (
    !currentOrganization &&
    user &&
    userOrganizations.length > 0 &&
    !isSettingOrg
  ) {
    console.log(
      "User has organizations but no current organization, attempting to set first organization"
    );
    console.log("Available organizations:", userOrganizations);

    // Set the first organization as current
    const firstOrg = userOrganizations[0];
    if (firstOrg && firstOrg.organization_id) {
      console.log(
        "Setting first organization as current:",
        firstOrg.organization_id
      );
      setIsSettingOrg(true);

      // Actually switch to the organization
      switchOrganization(firstOrg.organization_id).then((success) => {
        console.log("Organization switch result:", success);
        setIsSettingOrg(false);
      });

      return <LoadingScreen message="Setting up your workspace..." />;
    }
  }

  // If we're in the process of setting an organization, show loading
  if (isSettingOrg) {
    return <LoadingScreen message="Setting up your workspace..." />;
  }

  // If user has no current organization, show an error message
  if (!currentOrganization && user) {
    console.log(
      "No current organization found - this shouldn't happen after signup"
    );
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            No Organization Found
          </h1>
          <p className="text-gray-600 mb-4">
            You don't have access to any organizations. Please contact your
            administrator.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  console.log("Redirecting to dashboard");
  return <Navigate to={`/org/${currentOrganization!.id}/dashboard`} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </QueryClientProvider>
  );
}
